import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { AIPreselection } from './PageNavigatorPanel';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Sparkles,
  ChevronDown,
  ChevronRight,
  FileText,
  Layers,
  Check,
  AlertCircle,
  RotateCw,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type EditorBlock = {
  id: string;
  type: string;
  content: Record<string, unknown>;
};

type EditorPage = {
  id: string;
  name: string;
  blocks: EditorBlock[];
};

type Section = {
  markerId: string;
  title: string;
  color: string;
  pageId: string;
  pageName: string;
  blocks: EditorBlock[];
};

type RegeneratedBlock = {
  blockId: string;
  newText: string;
};

type AIRegenerationPanelProps = {
  pages: EditorPage[];
  onClose: () => void;
  onApply: (regeneratedBlocks: RegeneratedBlock[]) => void;
  apiBaseUrl?: string;
  userId?: string;
  token?: string;
  preselection?: AIPreselection | null;
  /** When true: renders as a plain flex column (sidebar mode). When false (default): fixed right overlay. */
  inline?: boolean;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const TEXT_BLOCK_TYPES = new Set([
  'text', 'paragraph', 'heading1', 'heading2', 'heading3',
  'quote', 'callout', 'badge', 'markdown',
]);

const DEFAULT_PROMPTS = [
  'Rewrite to be more professional and concise',
  'Make this more persuasive and client-focused',
  'Simplify the language for a non-technical audience',
  'Expand with more detail and supporting points',
  'Rewrite in a formal business tone',
  'Summarise into key bullet points',
  'Make the tone warmer and more engaging',
  'Remove any repetition and tighten the writing',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const extractText = (block: EditorBlock): string => {
  const c = block.content;
  if (typeof c.richText === 'string') return c.richText.replace(/<[^>]*>/g, '');
  if (typeof c.text === 'string') return c.text;
  if (typeof c.markdown === 'string') return c.markdown;
  if (typeof c.content === 'string') return c.content;
  return '';
};

const getSectionsFromPages = (pages: EditorPage[]): Section[] => {
  const sections: Section[] = [];
  for (const page of pages) {
    let currentSectionBlocks: EditorBlock[] = [];
    let currentMarker: { id: string; title: string; color: string } | null = null;

    for (const block of page.blocks) {
      if (block.type === 'section-marker') {
        // Push previous section if any
        if (currentMarker) {
          sections.push({
            markerId: currentMarker.id,
            title: currentMarker.title,
            color: currentMarker.color,
            pageId: page.id,
            pageName: page.name,
            blocks: currentSectionBlocks,
          });
        }
        currentMarker = {
          id: block.id,
          title: (block.content.title as string) || 'Untitled Section',
          color: (block.content.color as string) || '#3b82f6',
        };
        currentSectionBlocks = [];
      } else {
        currentSectionBlocks.push(block);
      }
    }
    // Push last section
    if (currentMarker) {
      sections.push({
        markerId: currentMarker.id,
        title: currentMarker.title,
        color: currentMarker.color,
        pageId: page.id,
        pageName: page.name,
        blocks: currentSectionBlocks,
      });
    }
  }
  return sections;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const AIRegenerationPanel: React.FC<AIRegenerationPanelProps> = ({
  pages,
  onClose,
  onApply,
  apiBaseUrl = '',
  userId = '',
  token = '',
  preselection = null,
  inline = false,
}) => {
  const [mode, setMode] = useState<'page' | 'section'>('page');
  const [selectedPageIds, setSelectedPageIds] = useState<Set<string>>(new Set());
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(new Set());

  // Auto-select when opened from the navigator ✨ button
  useEffect(() => {
    if (!preselection) return;
    if (preselection.type === 'page') {
      setMode('page');
      setSelectedPageIds(new Set([preselection.pageId]));
    } else {
      setMode('section');
      setSelectedSectionIds(new Set([preselection.markerId]));
    }
  }, [preselection]);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [preview, setPreview] = useState<RegeneratedBlock[]>([]);
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set(pages.map((p) => p.id)));

  const sections = useMemo(() => getSectionsFromPages(pages), [pages]);

  // Toggle helpers
  const togglePage = (id: string) =>
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSection = (id: string) =>
    setSelectedSectionIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleExpandPage = (id: string) =>
    setExpandedPages((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAllPages = () => setSelectedPageIds(new Set(pages.map((p) => p.id)));
  const clearAllPages = () => setSelectedPageIds(new Set());
  const selectAllSections = () => setSelectedSectionIds(new Set(sections.map((s) => s.markerId)));
  const clearAllSections = () => setSelectedSectionIds(new Set());

  // Build payload and call server
  const handleRegenerate = useCallback(async () => {
    if (!customPrompt.trim()) return;

    const targets =
      mode === 'page'
        ? pages.filter((p) => selectedPageIds.has(p.id))
        : sections.filter((s) => selectedSectionIds.has(s.markerId));

    if (targets.length === 0) return;

    setStatus('loading');
    setPreview([]);
    setErrorMessage('');

    const allRegenerated: RegeneratedBlock[] = [];

    for (const target of targets) {
      const targetBlocks = mode === 'page'
        ? (target as EditorPage).blocks
        : (target as Section).blocks;

      const textBlocks = targetBlocks
        .filter((b) => TEXT_BLOCK_TYPES.has(b.type))
        .map((b) => ({ blockId: b.id, type: b.type, text: extractText(b) }))
        .filter((b) => b.text.trim().length > 0);

      if (textBlocks.length === 0) continue;

      const targetName = mode === 'page'
        ? (target as EditorPage).name
        : (target as Section).title;

      try {
        const res = await fetch(`${apiBaseUrl}/api/scopingai/template-ai/regenerate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': 'scopingai',
            ...(userId ? { 'x-user-id': userId } : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            targetType: mode,
            targetName,
            textBlocks,
            prompt: customPrompt.trim(),
          }),
        });

        const data = (await res.json()) as {
          success: boolean;
          regeneratedBlocks?: RegeneratedBlock[];
          error?: string;
        };

        if (!data.success) throw new Error(data.error || 'Regeneration failed');
        allRegenerated.push(...(data.regeneratedBlocks || []));
      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
        return;
      }
    }

    setPreview(allRegenerated);
    setStatus('done');
  }, [mode, pages, sections, selectedPageIds, selectedSectionIds, customPrompt, apiBaseUrl, userId, token]);

  const handleApply = () => {
    onApply(preview);
    onClose();
  };

  const hasSelection = mode === 'page' ? selectedPageIds.size > 0 : selectedSectionIds.size > 0;
  const canRegenerate = hasSelection && customPrompt.trim().length > 0 && status !== 'loading';

  const content = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-blue-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">AI Regeneration</h2>
            <p className="text-xs text-gray-500">Rewrite pages or sections with AI</p>
          </div>
        </div>
        {!inline && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* Mode toggle */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">Regenerate by</p>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => { setMode('page'); setPreview([]); setStatus('idle'); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                mode === 'page'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              Pages
            </button>
            <button
              onClick={() => { setMode('section'); setPreview([]); setStatus('idle'); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                mode === 'section'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Layers className="w-4 h-4" />
              Sections
            </button>
          </div>
        </div>

        {/* Selection list */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-500">
              {mode === 'page' ? 'Select pages' : 'Select sections'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={mode === 'page' ? selectAllPages : selectAllSections}
                className="text-xs text-blue-500 hover:underline"
              >
                All
              </button>
              <button
                onClick={mode === 'page' ? clearAllPages : clearAllSections}
                className="text-xs text-gray-400 hover:underline"
              >
                None
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {mode === 'page' ? (
              /* Page list */
              pages.length === 0 ? (
                <p className="text-xs text-gray-400 p-3">No pages available</p>
              ) : (
                pages.map((page, i) => (
                  <label
                    key={page.id}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                      i !== 0 ? 'border-t border-gray-100' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPageIds.has(page.id)}
                      onChange={() => togglePage(page.id)}
                      className="w-4 h-4 rounded accent-blue-500"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{page.name}</p>
                      <p className="text-xs text-gray-400">
                        {page.blocks.filter((b) => TEXT_BLOCK_TYPES.has(b.type)).length} text block(s)
                      </p>
                    </div>
                    <span className="text-xs text-gray-300 flex-shrink-0">P{i + 1}</span>
                  </label>
                ))
              )
            ) : (
              /* Section list grouped by page */
              sections.length === 0 ? (
                <div className="p-4 text-center">
                  <Layers className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No sections found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Add a Section Divider block to create sections
                  </p>
                </div>
              ) : (
                pages.map((page) => {
                  const pageSections = sections.filter((s) => s.pageId === page.id);
                  if (pageSections.length === 0) return null;
                  const isExpanded = expandedPages.has(page.id);
                  return (
                    <div key={page.id}>
                      <button
                        onClick={() => toggleExpandPage(page.id)}
                        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors border-b border-gray-100"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        <FileText className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs font-semibold text-gray-600 truncate">{page.name}</span>
                        <span className="ml-auto text-xs text-gray-300">{pageSections.length}</span>
                      </button>
                      {isExpanded && pageSections.map((section, i) => (
                        <label
                          key={section.markerId}
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                            i !== 0 ? 'border-t border-gray-100' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSectionIds.has(section.markerId)}
                            onChange={() => toggleSection(section.markerId)}
                            className="w-4 h-4 rounded accent-blue-500"
                          />
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: section.color }}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800 truncate">{section.title}</p>
                            <p className="text-xs text-gray-400">
                              {section.blocks.filter((b) => TEXT_BLOCK_TYPES.has(b.type)).length} text block(s)
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  );
                })
              )
            )}
          </div>
        </div>

        {/* Prompt */}
        <div>
          {/* Default prompt dropdown */}
          <div className="relative mb-2">
            <button
              onClick={() => setShowPromptDropdown(!showPromptDropdown)}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <span className="text-xs text-gray-500">Quick prompts</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showPromptDropdown ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {showPromptDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden"
                >
                  {DEFAULT_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => {
                        setCustomPrompt(p);
                        setShowPromptDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors border-b border-gray-50 last:border-none"
                    >
                      {p}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <p className="text-xs font-medium text-gray-500 mb-1">Custom prompt</p>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Describe how you want the content rewritten…"
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-colors"
          />
        </div>

        {/* Error */}
        {status === 'error' && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        {/* Preview */}
        {status === 'done' && preview.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <p className="text-xs font-medium text-gray-700">
                {preview.length} block(s) regenerated — review below
              </p>
            </div>
            <div className="border border-green-200 rounded-lg bg-green-50 p-3 space-y-3 max-h-64 overflow-y-auto">
              {preview.map((b, i) => (
                <div key={b.blockId} className="bg-white rounded border border-green-100 p-2">
                  <p className="text-xs text-gray-400 mb-1">Block {i + 1}</p>
                  <p className="text-xs text-gray-700 whitespace-pre-wrap line-clamp-4">{b.newText}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {status === 'done' && preview.length === 0 && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
            No regeneratable text blocks found in the selected {mode === 'page' ? 'pages' : 'sections'}.
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 p-4 space-y-2 bg-white">
        {status === 'done' && preview.length > 0 ? (
          <div className="flex gap-2">
            <button
              onClick={() => { setStatus('idle'); setPreview([]); }}
              className="flex-1 py-2 px-3 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Discard
            </button>
            <button
              onClick={handleApply}
              className="flex-1 py-2 px-3 text-sm font-semibold text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              Apply Changes
            </button>
          </div>
        ) : (
          <button
            onClick={handleRegenerate}
            disabled={!canRegenerate}
            className={`w-full py-2.5 px-4 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
              canRegenerate
                ? 'bg-gradient-to-r from-violet-500 to-blue-500 text-white hover:from-violet-600 hover:to-blue-600 shadow-md hover:shadow-lg'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Regenerating…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Regenerate
                {hasSelection && (
                  <span className="text-xs opacity-75">
                    ({mode === 'page' ? selectedPageIds.size : selectedSectionIds.size})
                  </span>
                )}
              </>
            )}
          </button>
        )}
        {!hasSelection && (
          <p className="text-xs text-center text-gray-400">
            Select at least one {mode} above
          </p>
        )}
        {hasSelection && !customPrompt.trim() && (
          <p className="text-xs text-center text-gray-400">
            Enter a prompt above
          </p>
        )}
      </div>
    </>
  );

  if (inline) {
    return (
      <div className="flex flex-col h-full overflow-hidden bg-white">
        {content}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="fixed right-0 top-0 bottom-0 w-96 bg-white shadow-2xl border-l border-gray-200 flex flex-col z-50"
      style={{ maxWidth: '100vw' }}
    >
      {content}
    </motion.div>
  );
};
